import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  HStack,
  Checkbox,
  Box,
  useColorModeValue,
  Progress,
  Spinner
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { useUpdateModal } from '../../hooks/v3/useUpdateChecker';
import {
  currentVersionAtom,
  latestVersionInfoAtom,
  updateModalVisibleAtom
} from '../../atoms/v3/updateAtoms';

/**
 * 应用更新提醒 Modal
 * 只在 Android 原生平台显示
 */
export const UpdateModal: React.FC = () => {
  const { t } = useTranslation(['v3/update']);
  const [snoozeFor3Days, setSnoozeFor3Days] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const modalVisible = useAtomValue(updateModalVisibleAtom);
  const currentVersion = useAtomValue(currentVersionAtom);
  const latestVersionInfo = useAtomValue(latestVersionInfoAtom);

  const { handleUpdateNow, handleRemindLater, closeModal } = useUpdateModal();

  // 主题颜色
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'gray.100');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');

  if (!modalVisible || !latestVersionInfo) {
    return null;
  }

  const handleUpdate = async () => {
    if (latestVersionInfo.updateAction === 'electron') {
      setIsDownloading(true);
      try {
        await handleUpdateNow(latestVersionInfo.downloadUrl, latestVersionInfo.updateAction);
      } catch (error) {
        setIsDownloading(false);
        console.error('更新失败:', error);
      }
    } else {
      handleUpdateNow(latestVersionInfo.downloadUrl, latestVersionInfo.updateAction);
    }
  };

  // 根据更新动作获取按钮文本
  const getUpdateButtonText = () => {
    if (isDownloading && latestVersionInfo?.updateAction === 'electron') {
      return t('modal.downloading', '下载中...');
    }

    switch (latestVersionInfo?.updateAction) {
      case 'refresh':
        return t('modal.refreshNow', '立即刷新');
      case 'testflight':
        return t('modal.openTestFlight', '打开 TestFlight');
      case 'electron':
        return t('modal.installUpdate', '安装更新');
      case 'download':
      default:
        return t('modal.updateNow', '立即更新');
    }
  };

  const handleLater = () => {
    handleRemindLater(snoozeFor3Days);
  };

  return (
    <Modal
      isOpen={modalVisible}
      onClose={isDownloading ? () => {} : closeModal}
      isCentered
      closeOnOverlayClick={!isDownloading}
      closeOnEsc={!isDownloading}
      size={{ base: 'full', md: 'md' }}
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent
        bg={bgColor}
        borderColor={borderColor}
        borderWidth="1px"
        borderRadius={{ base: 0, md: 'xl' }}
        mx={{ base: 0, md: 4 }}
        my={{ base: 0, md: 'auto' }}
        maxH={{ base: 'calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))', md: '90vh' }}
        pt={{ base: 'env(safe-area-inset-top)', md: 4 }}
        pb={{ base: 'env(safe-area-inset-bottom)', md: 4 }}
        px={{ base: 4, md: 4 }}
      >
        <ModalHeader
          fontSize="lg"
          fontWeight="600"
          color={textColor}
          pb={2}
          pt={{ base: 4, md: 2 }}
          px={{ base: 6, md: 4 }}
          borderBottomWidth="1px"
          borderBottomColor={borderColor}
        >
          🚀 {t('modal.title', { version: latestVersionInfo.version })}
        </ModalHeader>

        <ModalBody py={6} px={{ base: 6, md: 6 }}>
          <VStack spacing={4} align="stretch">
            {/* 版本信息 */}
            <Box>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" color={secondaryTextColor}>
                  📦 {t('modal.currentVersion', { version: currentVersion })}
                </Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontSize="sm" color={secondaryTextColor}>
                  📦 {t('modal.latestVersion', { version: latestVersionInfo.version })}
                </Text>
              </HStack>
            </Box>

            {/* 更新内容 */}
            {latestVersionInfo.releaseNotes && (
              <Box>
                <Text fontSize="sm" fontWeight="500" color={textColor} mb={2}>
                  ✨ {t('modal.updateContent')}
                </Text>
                <Box
                  bg={useColorModeValue('gray.50', 'gray.700')}
                  borderRadius="md"
                  p={3}
                  borderWidth="1px"
                  borderColor={borderColor}
                  maxH="200px"
                  overflowY="auto"
                >
                  <Box
                    fontSize="sm"
                    color={secondaryTextColor}
                    lineHeight="1.5"
                    sx={{
                      '& h1, & h2, & h3': {
                        fontWeight: 'bold',
                        mb: 2
                      },
                      '& h2': {
                        fontSize: 'md'
                      },
                      '& h3': {
                        fontSize: 'sm'
                      },
                      '& p': {
                        mb: 2
                      },
                      '& ul, & ol': {
                        pl: 4,
                        mb: 2
                      },
                      '& li': {
                        mb: 1
                      },
                      '& a': {
                        color: 'blue.500',
                        textDecoration: 'underline'
                      }
                    }}
                    dangerouslySetInnerHTML={{ __html: latestVersionInfo.releaseNotes }}
                  />
                </Box>
              </Box>
            )}

            {/* 3天内不再提醒选项 */}
            <Box pt={2}>
              <Checkbox
                isChecked={snoozeFor3Days}
                onChange={(e) => setSnoozeFor3Days(e.target.checked)}
                colorScheme="blue"
                size="sm"
              >
                <Text fontSize="sm" color={secondaryTextColor}>
                  {t('modal.snoozeCheckbox')}
                </Text>
              </Checkbox>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter
          pt={4}
          pb={{ base: 6, md: 4 }}
          px={{ base: 6, md: 4 }}
          borderTopWidth="1px"
          borderTopColor={borderColor}
        >
          <HStack spacing={3} width="100%">
            {/* 下次提醒我按钮 */}
            <Button
              variant="ghost"
              colorScheme="gray"
              onClick={handleLater}
              flex={1}
              size="md"
              isDisabled={isDownloading}
            >
              {t('modal.remindLater')}
            </Button>

            {/* 立即更新按钮 */}
            <Button
              colorScheme="blue"
              onClick={handleUpdate}
              flex={1}
              size="md"
              fontWeight="600"
              isLoading={isDownloading}
              loadingText={t('modal.downloading', '下载中...')}
              isDisabled={isDownloading}
              leftIcon={isDownloading ? <Spinner size="sm" /> : undefined}
            >
              {getUpdateButtonText()}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
